import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import SocialMirrorLogo from '@/assets/socialMirror/SocialMirrorLogo.svg?react'
import SocialMirrorLogoDark from '@/assets/socialMirror/SocialMirrorLogoDark.svg?react'
import { AppRoutes } from '@/constants/appRoutes'
import env from '@/env/env'
import { useAppSelector } from '@/hooks/redux'
import useMobileMediaQuery from '@/hooks/useMobileMediaQuery'
import useSmallMobileMediaQuery from '@/hooks/useSmallMobileMediaQuery'
import useTabletMediaQuery from '@/hooks/useTabletMediaQuery'
import { selectIsDarkMode } from '@/store/api/ui/ui.selector'
import { useUpdateUserDataMutation } from '@/store/api/Users/Users.query'

import { NavigationMenu } from '../ui/navigation-menu'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
} from '../ui/select'

const OTP_COOLDOWN_SECONDS = 600 // 10 minutes
const LS_KEY = 'prospect_otp_sent_at'

const calculateTimeLeft = (): number => {
  const storedTimestamp = localStorage.getItem(LS_KEY)
  if (!storedTimestamp) return 0
  const elapsed = Math.floor(
    (Date.now() - parseInt(storedTimestamp, 10)) / 1000,
  )
  return Math.max(0, OTP_COOLDOWN_SECONDS - elapsed)
}

const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

const LogoContent = ({
  isDarkMode,
  isProduction,
  showDevLabel = false,
}: {
  isDarkMode: boolean
  isProduction: boolean
  showDevLabel?: boolean
}) => {
  const Logo = isDarkMode ? SocialMirrorLogoDark : SocialMirrorLogo

  if (!isProduction && showDevLabel) {
    return (
      <div className="flex flex-row items-center gap-2">
        <Logo className="flex-shrink-0 h-9 w-9" />
        <p className="text-base font-semibold tracking-tight">DEV</p>
      </div>
    )
  }

  return <Logo />
}

export const FormsScreensHeader = () => {
  const {
    t,
    i18n: { changeLanguage, language },
  } = useTranslation()

  const location = useLocation()
  const navigate = useNavigate()

  const [updateUserData, { isLoading }] = useUpdateUserDataMutation()
  const [selectedLanguage, setSelectedLanguage] = useState<string>(language)
  const isMobile = useMobileMediaQuery()
  const isSmallMobile = useSmallMobileMediaQuery()
  const isTablet = useTabletMediaQuery()

  const isMobileOrTablet = isMobile || isSmallMobile || isTablet

  const isVerificationRoute =
    location.pathname === AppRoutes.prospectEmailVerification

  const [timeLeft, setTimeLeft] = useState<number>(0)

  // Single interval: recalculates from localStorage every second
  useEffect(() => {
    if (!isVerificationRoute) {
      setTimeLeft(0)
      return
    }

    setTimeLeft(calculateTimeLeft())

    const interval = setInterval(() => {
      const remaining = calculateTimeLeft()
      setTimeLeft(remaining)

      if (remaining === 0 && localStorage.getItem(LS_KEY)) {
        localStorage.removeItem(LS_KEY)
        toast.error(t('setup.analysis.sessionExpired'))
        navigate(AppRoutes.revealDigitalFootprint, { replace: true })
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [isVerificationRoute, navigate, t])

  const handleLanguageChange = (newLanguage: string) => {
    setSelectedLanguage(newLanguage)
    changeLanguage(newLanguage)
    if (
      location.pathname !== AppRoutes.login &&
      location.pathname !== AppRoutes.registerStart
    ) {
      updateUserData({
        language: newLanguage,
      })
    }
  }

  const countdownBadge =
    isVerificationRoute && timeLeft > 0 ? (
      <div className="bg-baseColorHighlight border border-neutralColor10 rounded-xl px-4 py-2 shadow-sm max-sm:px-2 max-sm:py-1">
        <p
          className={`text-sm max-sm:text-xs font-medium ${
            timeLeft <= 60 ? 'text-dynamicRed' : 'text-neutralColor80'
          }`}
        >
          {t('signUp.emailVerification.otpExpiresIn', {
            time: formatTime(timeLeft),
          })}
        </p>
      </div>
    ) : null

  if (isMobileOrTablet) {
    return (
      <div className="lg:hidden flex flex-1 justify-between items-center mt-6 px-1">
        <div className="ml-1">{countdownBadge}</div>
        <Select onValueChange={handleLanguageChange}>
          <SelectTrigger
            className="rounded-3xl flex gap-2 text-xs h-8 mr-1 w-min"
            disabled={isLoading}
          >
            {selectedLanguage === 'es'
              ? t('dashboard.settings.spanish')
              : t('dashboard.settings.english')}
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem value="es">
                {t('dashboard.settings.spanish')}
              </SelectItem>
              <SelectItem value="en">
                {t('dashboard.settings.english')}
              </SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
    )
  }

  return (
    <div className="absolute my-6 flex flex-row justify-between items-center w-full">
      <div className="lg:ml-10 xl:ml-20">{countdownBadge}</div>
      <Select onValueChange={handleLanguageChange}>
        <SelectTrigger className="rounded-3xl w-min gap-2 lg:mr-10 xl:mr-20">
          {selectedLanguage === 'es'
            ? t('dashboard.settings.spanish')
            : t('dashboard.settings.english')}
        </SelectTrigger>
        <SelectContent className="text-xs ">
          <SelectGroup>
            <SelectItem value="es" className="w-min">
              {t('dashboard.settings.spanish')}
            </SelectItem>
            <SelectItem value="en">
              {t('dashboard.settings.english')}
            </SelectItem>
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
  )
}
